import React from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { Button } from 'antd'
import { tokenStore } from '../api'
import { useSessionValue } from '../hooks/use-session-value'
import { ArrowLeft } from './ArrowLeft'
import { CopyIcon } from './CopyIcon'
import { useToast } from './Toast'

/** Pull the `secret` query value out of an `otpauth://` URI. */
function extractSecret(otpauthUrl: string | null): string | null {
  if (!otpauthUrl) return null
  const match = otpauthUrl.match(/[?&]secret=([^&]+)/i)
  return match ? decodeURIComponent(match[1]) : null
}

/** Group the secret into 4-character blocks for readability. */
function formatSecret(secret: string): string {
  return secret.match(/.{1,4}/g)?.join(' ') ?? secret
}

/**
 * Manual TOTP setup key display. Reads the `otpauth://` URL cached by the QR
 * step, shows the secret, supports copy-to-clipboard, and advances to the
 * authenticator-app code entry. Falls back to a prompt when no setup is in
 * progress (e.g. the page was opened directly).
 */
export function ManualKeyPanel() {
  const { showToast } = useToast()
  const navigate = useNavigate()

  const otpauthUrl = useSessionValue(() => tokenStore.getOtpauthUrl())
  const secret = extractSecret(otpauthUrl)
  const [copied, setCopied] = React.useState(false)

  const handleCopy = async () => {
    if (!secret) return
    try {
      await navigator.clipboard.writeText(secret)
      setCopied(true)
      showToast('Setup key copied to clipboard', 'success')
    } catch {
      /* clipboard blocked — the key is still visible on screen */
    }
  }

  // No setup in progress — guide the user back to the QR step.
  if (!secret) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm leading-relaxed text-brand-copyright">
          We couldn&apos;t find a setup key. Start the authenticator setup again to generate one.
        </p>
        <Link to="/login/setup-authenticator">
          <Button type="primary" size="large" block>
            Back to authenticator setup
          </Button>
        </Link>
      </div>
    )
  }

  return (
    <>
      {/* Setup Key Display & Copy Section */}
      <section className="flex flex-col gap-3" aria-label="Setup Key Copy Controls">
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold text-brand-dark-text tracking-wider">
            Your setup key
          </label>
          <p className="text-[12px] text-brand-muted">
            Type this key exactly as shown — including spaces.
          </p>
        </div>

        {/* Copy Box Wrapper */}
        <div className="flex items-center gap-2 p-3 bg-brand-bg-light border border-brand-border-light rounded-lg">
          <span className="flex-1 font-mono text-sm font-bold bg-transparent border-none focus:outline-none text-brand-dark-text">
            {formatSecret(secret)}
          </span>
          <button
            type="button"
            id="btn-copy-setup-key"
            aria-label="Copy setup key to clipboard"
            onClick={handleCopy}
            className="p-1.5 text-brand-muted hover:text-brand-copyright hover:bg-brand-bg-light rounded transition-all"
          >
            <CopyIcon className={copied ? 'text-green-600' : 'text-brand-cyan'} />
          </button>
        </div>
      </section>

      {/* Controls Footer */}
      <footer className="flex items-center justify-between gap-3 mt-4">
        <span className="hidden lg:inline-flex">
          <Button
            type="link"
            icon={<ArrowLeft className="w-3.5 h-3.5" />}
            className="text-xs font-semibold !px-0"
            onClick={() => navigate({ to: '/login/setup-authenticator' })}
          >
            Back to QR Code
          </Button>
        </span>

        <div className="flex items-center gap-3 lg:flex-initial flex-1">
          <Button
            size="large"
            onClick={() => navigate({ to: '/login/setup-authenticator' })}
            className="!text-xs !font-semibold min-w-[80px] lg:flex-initial flex-1"
          >
            Cancel
          </Button>

          <Button
            type="primary"
            size="large"
            id="btn-key-setup-submit"
            onClick={() => navigate({ to: '/login/verify-app' })}
            className="!text-xs !font-medium min-w-[80px] lg:flex-initial flex-1"
          >
            Submit
          </Button>
        </div>
      </footer>
    </>
  )
}
